import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Modal, Button, Container, Row, Col, Card, Form, Spinner, Alert } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaCheck, FaTimes, FaFileArchive } from 'react-icons/fa';
import TitleWithBack from '../../components/common/TitleWithBack';

function ArchiverReviewPage() {
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [docChecks, setDocChecks] = useState({});
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [thumbPreviewUrls, setThumbPreviewUrls] = useState({}); // uploadId -> object URL, PDFs only
  const location = useLocation();
  console.log("Job object:", job);
  const assignmentId = location.state?.assignment?.assignmentId;

  // Helper: returns true if NOT an individual student work
  const isNotIndividualStudentWork = (upload) => {
    // Exclude documentType like 'Student_12345_Work'
    return !/^Student_.*_Work$/.test(upload.documentType);
  };

  const filteredUploads = job && job.uploads ? job.uploads.filter(isNotIndividualStudentWork) : [];

  useEffect(() => {
    if (assignmentId) {
      (async () => {
        try {
          const res = await axios.get(`/api/assignments/marked-as-done-by-assignment/${assignmentId}`);
          let data = res.data;
          if (Array.isArray(data)) {
            if (data.length > 0) {
              const base = { ...data[0] };
              const mergedUploads = data.reduce((uploads, curr) => uploads.concat(curr.uploads), []);
              base.uploads = mergedUploads;
              setJob(base);
              const checks = {};
              mergedUploads.forEach(u => { checks[u.uploadId] = false; });
              setDocChecks(checks);
            }
          } else {
            setJob(data);
            const checks = {};
            if (data.uploads && Array.isArray(data.uploads)) {
              data.uploads.forEach(u => { checks[u.uploadId] = false; });
            }
            setDocChecks(checks);
          }
        } catch (error) {
          console.error('Error fetching assignment data:', error);
        }
      })();
    }
  }, [assignmentId]);

  // Pre-fetch inline PDF previews for the document cards (zip/rar/7z files
  // get a plain icon instead - see the card render below).
  useEffect(() => {
    if (!job) return;
    let cancelled = false;
    const createdUrls = [];

    (async () => {
      for (const upload of filteredUploads) {
        const file = upload.files && upload.files.length > 0 ? upload.files[0] : null;
        if (!file || getFileExtension(file.fileName) !== 'pdf') continue;
        const blob = await fetchFileBlob(file.id, file.fileName);
        if (cancelled) return;
        if (blob && blob.size > 0) {
          const url = URL.createObjectURL(blob);
          createdUrls.push(url);
          setThumbPreviewUrls(prev => ({ ...prev, [upload.uploadId]: url }));
        }
      }
    })();

    return () => {
      cancelled = true;
      createdUrls.forEach(url => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job]);

  const handleToggle = (id) => {
    setDocChecks(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const allAccepted = filteredUploads.every(u => docChecks[u.uploadId]);

  const handleApprove = async () => {
    try {
      await axios.post(
        `/api/assignments/${assignmentId}/exam-centers/${job.examCentreId}/approve`,
        { jobId: job.assignmentId }
      );
      setFeedback({ 
        type: 'success', 
        message:  'job is approved'
      });
      setTimeout(() => navigate('/archiver/allocate/progress'), 2000);
    } catch (error) {
      console.error('Approve error:', error);
      setFeedback({ 
        type: 'danger', 
        message:  'error'
      });
      setTimeout(() => navigate('/archiver/allocate/progress'), 2000);
    }
  };

  const handleReject = () => {
    setShowReject(true);
  };

  const submitReject = async () => {
    try {
      await axios.post(
        `/api/assignments/${assignmentId}/exam-centers/${job.examCentreId}/reject`,
        { jobId: job.assignmentId, reason: rejectReason }
      );
      setFeedback({ 
        type: 'danger', 
        message:  'Rejected'
      });
      setTimeout(() => navigate('/archiver/allocate/progress'), 2000);
    } catch (error) {
      console.error('Reject error:', error);
      setFeedback({ 
        type: 'danger', 
        message:  'error'
      });
      setTimeout(() => navigate('/archiver/allocate/progress'), 2000);
    }
  };

  // Determine file extension from file name.
  const getFileExtension = (fileName) => {
    return fileName.split('.').pop().toLowerCase();
  };

  // Fetch file blob and override MIME type if needed.
  const fetchFileBlob = async (fileId, fileName) => {
    try {
      const response = await axios.get(
        `/api/assignments/${assignmentId}/exam-centers/${job.examCentreId}/documents/${fileId}/download`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        }
      );
      let contentType = response.headers['content-type'] || 'application/octet-stream';
      const ext = getFileExtension(fileName);
      // If the extension is pdf but content type is octet-stream, override it.
      if (ext === 'pdf' && contentType === 'application/octet-stream') {
        contentType = 'application/pdf';
      }
      const newBlob = new Blob([response.data], { type: contentType });
      console.log("Blob fetched: size=", newBlob.size, "type=", newBlob.type);
      return newBlob;
    } catch (error) {
      console.error('Error fetching file blob:', error);
      return null;
    }
  };

  // When preview is clicked, fetch the file blob and create an object URL.
  const handlePreview = async (upload) => {
    if (upload.files && upload.files.length > 0) {
      const file = upload.files[0];
      const blob = await fetchFileBlob(file.id, file.fileName);
      if (blob && blob.size > 0) {
        const objectUrl = URL.createObjectURL(blob);
        console.log("Object URL created:", objectUrl);
        setPreviewFile({ ...upload, previewUrl: objectUrl, fileName: file.fileName });
        setShowPreview(true);
      } else {
        console.error("Blob is empty or null");
      }
    }
  };

  // Render inline preview based on file extension.
  const renderPreviewContent = (previewUrl, upload) => {
    const ext = getFileExtension(upload.fileName);
    if (ext === 'pdf') {
      return <embed src={previewUrl} type="application/pdf" width="100%" height="500px" />;
    }
    return <img src={previewUrl} alt={upload.documentType} className="img-fluid" />;
  };

  if (!job) return (
    <Container className="my-4 text-center">
      <Spinner animation="border" variant="primary" />
    </Container>
  );

  return (
    <Container className="mt-2">
      <Row className="mb-4">
        <Col>
        <TitleWithBack title="Review and Approve Uploads" />
        <Row className="mb-3"></Row>
        </Col>
      </Row>
       {/* Feedback Message */}
   {feedback && (
    <Alert 
      variant={feedback.type === 'danger' ? 'danger' : 'success'} 
      className="mb-4"
      style={{ 
        position: 'fixed',
        top: '80px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        minWidth: '300px',
        textAlign: 'center',
        boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
      }}
    >
      {feedback.message}
    </Alert>
  )}

      <Row className="mb-4">
        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Header as="h5">Exam Details</Card.Header>
            <Card.Body>
              <p><strong>Module Name:</strong> {job.moduleName}</p>
              <p><strong>Exam Name:</strong> {job.examName}</p>
              <p><strong>Exam Centre:</strong> {job.examCentreName}</p>
              <p><strong>Exam Date:</strong> {job.examDate}</p>
              <p><strong>Assigned Date:</strong> {new Date(job.assignedDate).toLocaleDateString()}</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Header as="h5">Documents Checklist</Card.Header>
            <Card.Body>
            {filteredUploads.map((upload, i) => (
              <p key={i} className="mb-1"
              onClick={() => handleToggle(upload.uploadId)}
              style={{ cursor: 'pointer', userSelect: 'none' }}  >
               {i + 1}. {upload.documentType}
               {docChecks[upload.uploadId] && (
                 <span style={{ 
                   marginLeft: '8px',
                   color: 'green',
                   fontWeight: 'bold'
               }}>
                 <FaCheck />
                 </span>
               )}
              </p>
            ))}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row className="mb-4">
        {filteredUploads.map(upload => {
          const file = (upload.files && upload.files.length > 0) ? upload.files[0] : null;
          const fileName = file ? file.fileName : 'No file';
          const ext = file ? getFileExtension(file.fileName) : '';
          const isPdf = ext === 'pdf';
          const isArchive = ['zip', 'rar', '7z'].includes(ext);
          const pdfPreviewUrl = thumbPreviewUrls[upload.uploadId];
          return (
            <Col key={upload.uploadId} xs={6} sm={4} md={3} className="mb-3">
              <Card className="h-100 shadow-sm">
                <div
                  style={{ height: '150px', backgroundColor: '#e9ecef', overflow: 'hidden' }}
                  className="d-flex align-items-center justify-content-center px-2"
                >
                  {isPdf ? (
                    pdfPreviewUrl ? (
                      <embed
                        src={`${pdfPreviewUrl}#toolbar=0&navpanes=0&view=FitH`}
                        type="application/pdf"
                        width="100%"
                        height="100%"
                        style={{ pointerEvents: 'none' }}
                      />
                    ) : (
                      <Spinner animation="border" size="sm" variant="secondary" />
                    )
                  ) : isArchive ? (
                    <div className="text-center text-muted">
                      <FaFileArchive size={48} />
                      <div
                        className="small mt-1"
                        title={fileName}
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          overflowWrap: 'anywhere',
                          wordBreak: 'break-word'
                        }}
                      >
                        {fileName}
                      </div>
                    </div>
                  ) : (
                    <small
                      title={fileName}
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                        textAlign: 'center'
                      }}
                    >
                      {fileName}
                    </small>
                  )}
                </div>
                <Card.Body className="p-2 text-center">
                  <Card.Text
                    className="mb-1"
                    style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                  >
                    <strong>{upload.documentType}</strong>
                  </Card.Text>
                  <div className="d-flex justify-content-center">
                    <Button 
                      size="sm" 
                      variant={docChecks[upload.uploadId] ? "success" : "outline-secondary"} 
                      className="me-1" 
                      onClick={() => handleToggle(upload.uploadId)}
                    >
                      {docChecks[upload.uploadId] ? <FaCheck /> : <FaTimes />}
                    </Button>
                    <Button size="sm" variant="primary" onClick={() => handlePreview(upload)}>Preview</Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
      <Row className="mb-4">
        <Col className="text-center">
          {allAccepted ? (
            <Button variant="success" size="lg" onClick={handleApprove}>APPROVE WORK</Button>
          ) : (
            <Button variant="danger" size="lg" onClick={handleReject}>REJECT</Button>
          )}
        </Col>
      </Row>
      {showReject && (
        <Row className="mb-4">
          <Col>
            <Form.Group controlId="rejectReason">
              <Form.Label>Rejection Reason</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3} 
                value={rejectReason} 
                onChange={(e) => setRejectReason(e.target.value)} 
                placeholder="Enter rejection reason" 
              />
            </Form.Group>
            <div className="mt-2 text-center">
              <Button variant="danger" onClick={submitReject}>Confirm Reject</Button>
            </div>
          </Col>
        </Row>
      )}
      <Modal show={showPreview} onHide={() => setShowPreview(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>File Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {previewFile && previewFile.previewUrl ? (
            renderPreviewContent(previewFile.previewUrl, previewFile)
          ) : (
            <p>Nothing to show here</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <a 
            href={previewFile && previewFile.previewUrl ? previewFile.previewUrl : '#'} 
            download={previewFile && previewFile.fileName ? previewFile.fileName : 'download'} 
            style={{ textDecoration: 'none' }}
          >
            <Button variant="primary">Download</Button>
          </a>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default ArchiverReviewPage;
